import { hasLiveSupportForCountry } from './supportCards';
import { getAustraliaStaticSupportPlaybook } from './supportAustraliaPlaybooks';
import { getNewZealandStaticSupportPlaybook } from './supportNewZealandPlaybooks';
import { getUnitedStatesStaticSupportPlaybook } from './supportUsPlaybooks';

export interface StaticSupportPlaybookLink {
  label: string;
  url: string;
}

export interface StaticSupportPlaybookStep {
  step: number;
  title: string;
  description: string;
  actions: string[];
  links?: StaticSupportPlaybookLink[];
  call_script?: string | null;
  tip?: string | null;
}

export interface StaticSupportPlaybook {
  id: string;
  slug: string;
  section: string;
  title: string;
  description: string;
  icon: string;
  difficulty: string | null;
  estimated_time: string | null;
  life_event_tags: string[] | null;
  steps: StaticSupportPlaybookStep[];
  is_active: boolean;
  sort_order: number;
  last_reviewed_at: string;
  review_frequency_months: number;
  created_at: string;
  updated_at: string;
}

type StaticSupportPlaybookStepInput = Omit<StaticSupportPlaybookStep, 'step'>;

interface StaticSupportPlaybookDefinition {
  slug: string;
  title: string;
  description: string;
  icon: string;
  difficulty?: string | null;
  estimated_time?: string | null;
  life_event_tags?: string[] | null;
  steps: StaticSupportPlaybookStepInput[];
}

type StaticSupportPlaybookBuilder = (
  countryCode?: string | null,
  postcode?: string | null,
) => StaticSupportPlaybook | null;

const PLAYBOOK_REVIEWED_AT = '2026-04-20T12:00:00.000Z';

const buildStaticSupportPlaybook = (
  definition: StaticSupportPlaybookDefinition,
): StaticSupportPlaybook => ({
  id: `static-${definition.slug}`,
  slug: definition.slug,
  section: 'support',
  title: definition.title,
  description: definition.description,
  icon: definition.icon,
  difficulty: definition.difficulty ?? 'moderate',
  estimated_time:
    definition.estimated_time ?? `${definition.steps.length} step${definition.steps.length === 1 ? '' : 's'}`,
  life_event_tags: definition.life_event_tags ?? null,
  steps: definition.steps.map((step, index) => ({
    ...step,
    step: index + 1,
  })),
  is_active: true,
  sort_order: 0,
  last_reviewed_at: PLAYBOOK_REVIEWED_AT,
  review_frequency_months: 6,
  created_at: PLAYBOOK_REVIEWED_AT,
  updated_at: PLAYBOOK_REVIEWED_AT,
});

const buildIrelandOnlyPlaybook = (
  definition: StaticSupportPlaybookDefinition,
): StaticSupportPlaybookBuilder => (countryCode) =>
  countryCode === 'IE' ? buildStaticSupportPlaybook(definition) : null;

const buildSingleDadsStartingPointPlaybook: StaticSupportPlaybookBuilder = (
  countryCode,
) => {
  if (countryCode !== 'GB' && countryCode !== 'IE') {
    return null;
  }

  const isIreland = countryCode === 'IE';

  const guardianshipLinks = isIreland
    ? [
        { label: 'Treoir: Guardianship', url: 'https://treoir.ie/information/guardianship/' },
        {
          label: 'gov.ie: Custody of children',
          url: 'https://www.gov.ie/en/department-of-justice-home-affairs-and-migration/services/custody-of-children/',
        },
      ]
    : [
        {
          label: 'GOV.UK: Parental rights and responsibilities',
          url: 'https://www.gov.uk/parental-rights-responsibilities',
        },
        {
          label: 'Citizens Advice: Sorting out parental responsibility',
          url: 'https://www.citizensadvice.org.uk/family/',
        },
      ];

  const moneyLinks = isIreland
    ? [
        {
          label: 'gov.ie: Child maintenance',
          url: 'https://www.gov.ie/en/department-of-justice-home-affairs-and-migration/campaigns/child-maintenance/',
        },
        {
          label: 'Treoir: Financial support and social welfare benefits',
          url: 'https://www.treoir.ie/information/financial-support-social-welfare-benefits-2/',
        },
      ]
    : [
        {
          label: 'GOV.UK: Child Maintenance Service',
          url: 'https://www.gov.uk/child-maintenance-service',
        },
        {
          label: 'Gingerbread: Child maintenance',
          url: 'https://www.gingerbread.org.uk/',
        },
      ];

  const supportLinks = isIreland
    ? [
        { label: 'One Family', url: 'https://onefamily.ie/' },
        { label: 'Treoir: Fathers', url: 'https://treoir.ie/groups/father/' },
      ]
    : [{ label: 'Families Need Fathers', url: 'https://fnf.org.uk/' }];

  const conflictLinks = isIreland
    ? [
        { label: 'Treoir: Fathers', url: 'https://treoir.ie/groups/father/' },
        { label: 'Find a Citizens Information Centre', url: 'https://centres.citizensinformation.ie/' },
      ]
    : [
        {
          label: 'Citizens Advice: Family problems',
          url: 'https://www.citizensadvice.org.uk/family/',
        },
        {
          label: 'GOV.UK: Child arrangements',
          url: 'https://www.gov.uk/looking-after-children-divorce',
        },
      ];

  return buildStaticSupportPlaybook({
    slug: 'single-dads-starting-point',
    title: 'Single Dads: A Starting Point',
    description:
      'Practical first steps for dads steadying rights, routines, and support after separation.',
    icon: '🧢',
    life_event_tags: ['single dads', 'separation', 'co-parenting'],
    steps: [
      {
        title: 'Get the basics in one place',
        description:
          'Make one live note with school or nursery contacts, GP details, medication, allergies, pickup arrangements, overnight routines, and key dates. When life feels tense, one reliable source of truth cuts down avoidable arguments and missed details.',
        actions: [
          'Write down school, nursery, and GP contact details.',
          'List pickup permissions, medication, allergies, and emergency contacts.',
          'Keep one current note of handover times, clubs, and recurring child-care arrangements.',
        ],
        tip: 'You do not need a perfect system. One calm, current note is far better than trying to hold everything in your head.',
      },
      {
        title: 'Check guardianship, parental responsibility, and key paperwork',
        description:
          'If you were not married to the child’s mother, it is worth checking your legal decision-making status early. The exact route differs between the UK and Ireland, but it affects school, medical, passport, and major decision-making access, and it is easier to deal with this before a conflict hardens.',
        actions: [
          'Check whether you already have parental responsibility or guardianship.',
          'Keep copies of birth certificates, court orders, and any written arrangements together.',
          'If something important is unclear, get advice before the next school, medical, or travel decision lands.',
        ],
        links: guardianshipLinks,
      },
      {
        title: 'Sort money early, even if it feels awkward',
        description:
          'Child-related costs get stressful quickly when no one has named who is paying for what. Clarify child maintenance, shared costs, transport, clubs, uniforms, and anything that is already becoming a repeated argument.',
        actions: [
          'List the regular child-related costs you are already covering.',
          'Separate urgent essentials from the things that can wait.',
          'Use official calculators or advice routes before agreeing to something you cannot sustain.',
        ],
        links: moneyLinks,
      },
      {
        title: 'Build support that actually feels usable as a dad',
        description:
          'A lot of dads do not need a big social circle. They need one or two places where they can ask practical questions without feeling side-lined. Start with the smallest useful step rather than waiting until you feel fully settled.',
        actions: [
          'Join the Single Dads channel in Community and ask one practical question this week.',
          'Tell one reliable friend, relative, or another parent what kind of backup would genuinely help.',
          'Save one dad-friendly organisation or advice route now so you are not searching in the middle of a bad day.',
        ],
        links: supportLinks,
        tip: 'Feeling isolated does not mean the app is not for you. It usually means you need lower-pressure entry points, not more noise.',
      },
      {
        title: 'Get advice early if contact or safety becomes a fight',
        description:
          'If handovers, communication, withholding contact, or safeguarding concerns are becoming a pattern, get proper advice early. Early notes, timelines, and calm evidence are more useful than trying to reconstruct everything later.',
        actions: [
          'Keep a factual timeline of incidents, missed handovers, and important messages.',
          'Use written communication where possible when things are escalating.',
          'Get specialist advice quickly if a child may be at risk or contact is being disrupted.',
        ],
        links: conflictLinks,
        call_script:
          'I am a single dad and I need practical advice about parental responsibility or guardianship, child arrangements, and what I should do first. Can you tell me the next steps and which documents I should have ready?',
      },
    ],
  });
};

const IRELAND_HELPER_PACK_BUILDERS: Record<string, StaticSupportPlaybookBuilder> = {
  'moving-house': buildIrelandOnlyPlaybook({
    slug: 'moving-house',
    title: 'Moving House as a Single Parent',
    description:
      'The full checklist from deciding to move to settling in without missing the practical Irish admin.',
    icon: '📦',
    life_event_tags: ['housing', 'moving', 'single parent'],
    steps: [
      {
        title: 'Check the tenancy paperwork first',
        description:
          'If you are renting, check exactly how your tenancy is ending. If you are giving notice, do it in writing. If your landlord is ending the tenancy, check that the notice is valid and that it was also copied to the RTB.',
        actions: [],
        links: [
          {
            label: 'RTB: Ending a tenancy',
            url: 'https://rtb.ie/renting/ending-a-tenancy/notice-of-termination-guides/',
          },
        ],
      },
      {
        title: 'Tell key services early',
        description:
          'Update the Department of Social Protection, Revenue, your bank, insurers, employer, school or childcare provider, GP, and any specialist services involved with your child.',
        actions: [],
      },
      {
        title: 'Update housing support quickly',
        description:
          'If you are on HAP, Rent Supplement, or a social housing waiting list, tell the council or Community Welfare Service as early as possible. A move can affect the support, the limits, or the documents they need from you.',
        actions: [],
        links: [
          {
            label: 'gov.ie: Housing Assistance Payment',
            url: 'https://www.gov.ie/en/department-of-housing-local-government-and-heritage/services/housing-assistance-payment/',
          },
        ],
      },
      {
        title: 'Sort school changes before the move',
        description:
          'If you are changing school, contact the new school or admissions team as early as possible. If your child has additional needs, tell the current school and your local SENO if school placement, transport, or assistive technology may be affected.',
        actions: [],
      },
      {
        title: 'Record the property condition',
        description:
          'Take dated photos and meter readings when you leave. Keep copies of all emails and texts about keys, cleaning, repairs, and your deposit.',
        actions: [],
      },
      {
        title: 'Redirect your address everywhere important',
        description:
          'Make a deliberate list: DSP/MyWelfare, Revenue, school, GP, hospital appointments, childcare, utilities, employer, bank, insurer, and subscription services.',
        actions: [],
      },
      {
        title: 'Challenge deposit issues quickly',
        description:
          'If your deposit is not returned or deductions look unfair, challenge it fast in writing. Threshold can help, and the RTB dispute route may be needed.',
        actions: [],
        links: [{ label: 'Threshold: Housing advice', url: 'https://threshold.ie/get-help/' }],
      },
      {
        title: 'Re-check your supports after the move',
        description:
          'Housing, childcare, medical card eligibility, school transport, and local services can all change after a move. Re-run the practical support check once the new address is settled.',
        actions: [],
      },
      {
        title: 'Make the first week a safe landing',
        description:
          'The first goal is not a perfect home. It is a safe landing. Unpack what the children need first, keep meals simple, and protect routine where you can.',
        actions: [],
      },
    ],
  }),
  'going-self-employed': buildIrelandOnlyPlaybook({
    slug: 'going-self-employed',
    title: 'Setting Up Self-Employed from Home',
    description:
      'Everything to do in the first 90 days so you are legal, organised, and not blindsided by tax.',
    icon: '💻',
    life_event_tags: ['work', 'self-employment', 'tax'],
    steps: [
      {
        title: 'Choose the simplest structure that fits',
        description:
          'Most people starting small begin as sole traders. Do not overcomplicate the first step unless there is a clear reason to set up a company.',
        actions: [],
      },
      {
        title: 'Register for tax with Revenue',
        description:
          'You need a PPSN before you can register. Your PPSN becomes your tax reference number once Revenue registration is complete.',
        actions: [],
        links: [
          {
            label: 'Revenue: Register as a sole trader',
            url: 'https://www.revenue.ie/en/starting-a-business/registering-for-tax/how-to-register-for-tax-as-a-sole-trader.aspx',
          },
        ],
      },
      {
        title: 'Separate business money early',
        description:
          'It is not about looking professional. It is about making tax, expenses, and cash flow legible when life gets busy.',
        actions: [],
      },
      {
        title: 'Learn the taxes that shape your cash flow',
        description:
          'Income tax, PRSI, USC, and preliminary tax are the four numbers to understand early if you want to avoid a bill you cannot pay.',
        actions: [],
      },
      {
        title: 'Track expenses from day one',
        description:
          'Keep receipts and records for costs that are directly related to running the business. Revenue expects records, not reconstructed guesses.',
        actions: [],
        links: [
          {
            label: 'Revenue: Business expenses',
            url: 'https://www.revenue.ie/en/starting-a-business/claiming-a-deduction-for-expenses/index.aspx',
          },
        ],
      },
      {
        title: 'Set up ROS before deadline season',
        description:
          'Set up your Revenue Online Service access before deadline season. It is much easier to do this when you are calm than when you are already late.',
        actions: [],
      },
      {
        title: 'Check what happens to your supports',
        description:
          'If you are parenting alone, self-employment can affect One-Parent Family Payment, Jobseeker’s Transitional Payment, medical card eligibility, and childcare supports. Check before income becomes irregular.',
        actions: [],
      },
      {
        title: 'Know the fallback if work stops',
        description:
          'If you have paid the right PRSI, Jobseeker’s Benefit (Self-Employed) may matter. If money gets tight first, MABS and Additional Needs Payments may be more relevant than waiting until things get critical.',
        actions: [],
        links: [
          {
            label: 'gov.ie: Jobseeker’s Benefit (Self-Employed)',
            url: 'https://www.gov.ie/en/department-of-social-protection/services/jobseekers-benefit-for-the-self-employed/',
          },
        ],
      },
      {
        title: 'Build a weekly admin habit',
        description:
          'Thirty minutes a week is usually enough to keep income, receipts, mileage, and deadlines under control. The aim is steady admin, not heroic catch-up.',
        actions: [],
      },
    ],
  }),
  'separation-divorce': buildIrelandOnlyPlaybook({
    slug: 'separation-divorce',
    title: 'Separation and Relationship Breakdown: First Steps',
    description:
      'What to do in the first weeks when everything feels impossible, with Irish family-law basics and support routes.',
    icon: '⚖️',
    life_event_tags: ['separation', 'divorce', 'co-parenting'],
    steps: [
      {
        title: 'Get safe first if you need to',
        description:
          'If there is any immediate risk to you or your children, call 999 or 112. Women’s Aid and the courts’ domestic violence guides are practical starting points if safety, coercion, or fear are part of what is happening.',
        actions: [],
      },
      {
        title: 'Tell school or childcare what matters',
        description:
          'They need to know who can collect the children, who should receive communications, and whether there are any safety concerns about handovers or contact.',
        actions: [],
      },
      {
        title: 'Secure your finances carefully',
        description:
          'Make sure you can access money in your own name. Save copies of key financial information. Do not hide or move money recklessly, but do make sure you understand the household position.',
        actions: [],
      },
      {
        title: 'Update key payments and supports',
        description:
          'Child Benefit, One-Parent Family Payment, Jobseeker’s Transitional Payment, Working Family Payment, medical card status, and childcare supports may all need updating.',
        actions: [],
      },
      {
        title: 'Check guardianship early',
        description:
          'This matters especially if you were not married. In Ireland, an unmarried father does not automatically have guardianship rights. Do not assume you are on the same legal footing without checking.',
        actions: [],
        links: [
          {
            label: 'Treoir: Guardianship information',
            url: 'https://treoir.ie/information/guardianship-information/',
          },
        ],
      },
      {
        title: 'Put the children’s arrangements in writing',
        description:
          'A simple written plan is better than repeated arguments. Keep it factual: where the children will be, handovers, school decisions, holidays, and how you will communicate.',
        actions: [],
      },
      {
        title: 'Know the maintenance options',
        description:
          'Some parents manage this by agreement or mediation. Others need a maintenance order. Since June 2024, child maintenance is no longer counted in the means test for social welfare payments.',
        actions: [],
      },
      {
        title: 'Understand the divorce basics if you are married',
        description:
          'Ireland has no-fault divorce, but there is still a legal process and the court must be satisfied that proper provision is made for spouses and dependent children.',
        actions: [],
        links: [
          {
            label: 'Courts: Marriage, separation and divorce',
            url: 'https://www.courts.ie/hubs/marriage--separation-and-divorce',
          },
        ],
      },
      {
        title: 'Use mediation and legal help early',
        description:
          'The Legal Aid Board family mediation service can help with parenting, money, and the family home. If court may be unavoidable, getting legal advice early usually saves pain later.',
        actions: [],
        links: [
          {
            label: 'Legal Aid Board: Family mediation',
            url: 'https://www.legalaidboard.ie/our-family-mediation-service/',
          },
        ],
      },
      {
        title: 'Treat your mental health as real admin',
        description:
          'Separation is both an emotional event and an admin event. Treat both as real. One Family, Samaritans, HSE talk therapy, and your GP are legitimate supports here.',
        actions: [],
      },
    ],
  }),
  'getting-ehcp': buildIrelandOnlyPlaybook({
    slug: 'getting-ehcp',
    title: 'Getting Supports for Your Child’s Additional Needs',
    description:
      'The process explained in plain English, with the Irish routes that matter most.',
    icon: '📚',
    life_event_tags: ['education', 'childcare', 'additional needs'],
    steps: [
      {
        title: 'Start with what is happening day to day',
        description:
          'Write down what you are seeing at home, in childcare, or in school. Concrete examples matter more than labels at the start.',
        actions: [],
      },
      {
        title: 'Talk to school or childcare early',
        description:
          'Ask what support is already in place, what the teacher or SEN lead is noticing, and what changes they can make now instead of only waiting for an external assessment.',
        actions: [],
      },
      {
        title: 'Use your GP when referral is needed',
        description:
          'For many families, the GP is the practical starting point for referral, reports, and local service signposting.',
        actions: [],
      },
      {
        title: 'Find your local SENO',
        description:
          'In Ireland, your SENO is often the key contact for access to educational supports, school transport, assistive technology, and special-class or special-school questions.',
        actions: [],
        links: [{ label: 'NCSE: Find your SENO', url: 'https://www.ncse.ie/parents/support-network/seno/' }],
      },
      {
        title: 'Understand Assessment of Need clearly',
        description:
          'Assessment of Need is an HSE-led statutory process. It can be important, but it is not the only route to support and children do not need an Assessment of Need report to receive every service.',
        actions: [],
        links: [
          {
            label: 'gov.ie: Assessment of Need changes',
            url: 'https://www.gov.ie/en/department-of-children-disability-and-equality/press-releases/government-moves-to-improve-assessment-of-need-process-for-children-and-families/',
          },
        ],
      },
      {
        title: 'Ask what school support can happen now',
        description:
          'Mainstream classroom supports, special education teaching, assistive technology, and local school planning often matter before any big formal process finishes.',
        actions: [],
      },
      {
        title: 'Ask direct questions about placement',
        description:
          'If you think your child needs a special class or special school placement, ask what the pathway is, who is making the application, and what the timeline is in your area.',
        actions: [],
      },
      {
        title: 'Check transport and technology support',
        description:
          'Where a child’s needs affect travel or access to the curriculum, school transport and assistive technology may be relevant and usually involve the school plus the SENO.',
        actions: [],
        links: [
          {
            label: 'gov.ie: School transport',
            url: 'https://www.gov.ie/en/department-of-education/services/school-transport/',
          },
        ],
      },
      {
        title: 'Review family income supports too',
        description:
          'If care needs are substantial, Domiciliary Care Allowance, Carer’s Allowance, or the Carer’s Support Grant may matter as much as the school process.',
        actions: [],
      },
      {
        title: 'Keep every letter and date',
        description:
          'Irish systems move more smoothly when you can show the timeline. A simple notebook or folder helps more than parents expect.',
        actions: [],
      },
    ],
  }),
  'going-back-to-work': buildIrelandOnlyPlaybook({
    slug: 'going-back-to-work',
    title: 'Going Back to Work After a Gap',
    description:
      'Whether it has been 6 months or 6 years, this helps you approach work again without panic.',
    icon: '💼',
    life_event_tags: ['work', 'childcare', 'returning to work'],
    steps: [
      {
        title: 'Work out what you need to bring home',
        description:
          'Before you apply, calculate the minimum that makes work viable after travel, tax, and childcare. A job can look fine on paper and still leave you worse off if the childcare maths is wrong.',
        actions: [],
        links: [
          {
            label: 'Citizens Information: Returning to work',
            url: 'https://www.citizensinformation.ie/en/employment/starting-work-and-changing-job/training-and-looking-for-work/return-to-work/',
          },
        ],
      },
      {
        title: 'Check what support changes when you work',
        description:
          'Working Family Payment, the National Childcare Scheme, ECCE, medical card eligibility, One-Parent Family Payment, or Jobseeker’s Transitional Payment may all change once work starts.',
        actions: [],
        links: [{ label: 'National Childcare Scheme', url: 'https://www.ncs.gov.ie/' }],
      },
      {
        title: 'Sort childcare before you say yes',
        description:
          'Do not rely on hope. Check the actual place, the actual hours, the holiday problem, and the sick-day backup before you accept the job.',
        actions: [],
      },
      {
        title: 'Update your CV simply and honestly',
        description:
          'A straightforward line about parenting and family responsibilities is enough. The goal is not to over-explain, it is to move the conversation quickly to your value now.',
        actions: [],
      },
      {
        title: 'Ask how flexibility works in practice',
        description:
          'The right to request flexible or remote working matters, but workplace culture matters too. Ask how flexibility works in practice for parents.',
        actions: [],
      },
      {
        title: 'Know the leave rights that protect you',
        description:
          'Force majeure leave, domestic violence leave, and statutory sick leave all matter more when you are parenting without a second adult in the house.',
        actions: [],
      },
      {
        title: 'Check whether Back to Work Family Dividend helps',
        description:
          'If you are moving from a qualifying social welfare payment into work, this payment can soften the first years of transition.',
        actions: [],
        links: [
          {
            label: 'gov.ie: Back to Work Family Dividend',
            url: 'https://www.gov.ie/en/department-of-social-protection/services/back-to-work-family-dividend/',
          },
        ],
      },
      {
        title: 'Give the first month more margin',
        description:
          'The admin, the children’s adjustment, and your own nervous system all need runway. Keep the first month lighter than normal if you can.',
        actions: [],
      },
    ],
  }),
  'christmas-one-income': buildIrelandOnlyPlaybook({
    slug: 'christmas-one-income',
    title: 'Christmas and School Holidays on One Income',
    description:
      'Planning ahead so it does not hit you all at once, with Irish support routes and school-holiday reality in mind.',
    icon: '🎄',
    life_event_tags: ['money', 'school holidays', 'budgeting'],
    steps: [
      {
        title: 'Start a small savings pot early',
        description:
          'A tiny automatic transfer beats a heroic December rescue. Naming the pot often helps more than the amount.',
        actions: [],
      },
      {
        title: 'Use the supports already there',
        description:
          'Check school meals, the Back to School Clothing and Footwear Allowance, local community supports, library events, and school-holiday schemes before spending money you do not have.',
        actions: [],
        links: [
          {
            label: 'Citizens Information: Help with school costs',
            url: 'https://www.citizensinformation.ie/en/education/primary-and-post-primary-education/educational-supports/financial-help-with-school/',
          },
        ],
      },
      {
        title: 'Use childcare supports strategically',
        description:
          'If school holidays are the expensive part of the year, check what the National Childcare Scheme or registered school-age services can reduce.',
        actions: [],
        links: [{ label: 'National Childcare Scheme', url: 'https://www.ncs.gov.ie/' }],
      },
      {
        title: 'Plan the free things first',
        description:
          'Parks, libraries, beaches, museums, playgrounds, community events, and time with other families usually matter more than expensive days out.',
        actions: [],
      },
      {
        title: 'Make food planning boring and effective',
        description:
          'Own-brand, freezer food, batch cooking, and a simple shopping list do more for peace than trying to create a picture-perfect holiday.',
        actions: [],
      },
      {
        title: 'Keep expectations honest',
        description:
          'Children usually cope better with a clear, calm limit than with a stressed parent over-promising and then panicking.',
        actions: [],
      },
      {
        title: 'Protect January as well as December',
        description:
          'The real goal is not a perfect Christmas. It is getting through the holidays without starting the new year frightened by debt.',
        actions: [],
        links: [{ label: 'MABS', url: 'https://www.mabs.ie/' }],
      },
    ],
  }),
  'renting-goes-wrong': buildIrelandOnlyPlaybook({
    slug: 'renting-goes-wrong',
    title: 'Your Rights When Renting Goes Wrong',
    description:
      'What to do when your landlord is failing you, step by step, using the RTB and Irish tenancy rules.',
    icon: '🏠',
    life_event_tags: ['housing', 'renting', 'tenancy'],
    steps: [
      {
        title: 'Put everything in writing first',
        description:
          'Repairs, arrears conversations, deposit disputes, and notice issues all become much easier if there is a written record.',
        actions: [],
      },
      {
        title: 'Check whether the notice is valid',
        description:
          'A notice of termination has to meet Irish rules. One of the biggest checks is whether it was also sent to the RTB on the same day as to you.',
        actions: [],
        links: [
          {
            label: 'Citizens Information: If your landlord wants you to leave',
            url: 'https://www.citizensinformation.ie/en/housing/renting-a-home/tenants-rights-and-responsibilities/if-your-landlord-wants-you-to-leave/',
          },
        ],
      },
      {
        title: 'Use the minimum standards and repair rules',
        description:
          'Do not argue vaguely about whether a property is good enough. Focus on the standards, the specific repair, and the timeline.',
        actions: [],
      },
      {
        title: 'Act before rent arrears spiral',
        description:
          'If rent is slipping, ask early whether HAP, Rent Supplement, MABS, or a payment-plan conversation is possible. Silence makes everything worse.',
        actions: [],
      },
      {
        title: 'Challenge unfair deposit deductions',
        description:
          'Deposits can be kept for certain reasons, but not just because a landlord feels like it. Photographs, check-out records, and Threshold advice matter.',
        actions: [],
      },
      {
        title: 'Use the RTB when needed',
        description:
          'If the issue is serious or unresolved, RTB dispute resolution is the formal route. Keep documents in one place so you can move quickly if needed.',
        actions: [],
        links: [{ label: 'RTB: Disputes', url: 'https://rtb.ie/disputes/' }],
      },
      {
        title: 'Get human help before crisis point',
        description:
          'Threshold is often the best first human contact. They can help you understand your rights and the next safest move.',
        actions: [],
        links: [{ label: 'Threshold: Get help', url: 'https://threshold.ie/get-help/' }],
      },
    ],
  }),
  'benefits-check': buildIrelandOnlyPlaybook({
    slug: 'benefits-check',
    title: 'Benefits Check: Are You Missing Money?',
    description:
      'The Irish supports single parents most often miss, and the order that is safest to check them in.',
    icon: '💰',
    life_event_tags: ['money', 'benefits', 'single parent'],
    steps: [
      {
        title: 'Start with Child Benefit',
        description:
          'This is the most universal payment and the one that often needs updating early after a birth or separation.',
        actions: [],
      },
      {
        title: 'Check OFP or JST next',
        description:
          'If you are parenting alone, these are the first core income supports to understand based on your youngest child’s age.',
        actions: [],
        links: [
          {
            label: 'gov.ie: One-Parent Family Payment',
            url: 'https://www.gov.ie/en/department-of-social-protection/services/one-parent-family-payment/',
          },
        ],
      },
      {
        title: 'If you are working, check WFP',
        description:
          'Plenty of parents do not realise that low-paid work can still carry weekly family support.',
        actions: [],
        links: [
          {
            label: 'gov.ie: Working Family Payment',
            url: 'https://www.gov.ie/en/department-of-social-protection/services/working-family-payment-wfp/',
          },
        ],
      },
      {
        title: 'Check childcare properly',
        description:
          'The National Childcare Scheme and ECCE can make more difference to take-home income than parents expect.',
        actions: [],
      },
      {
        title: 'Check school and seasonal supports',
        description:
          'School Meals and the Back to School Clothing and Footwear Allowance are practical supports, not bonus extras.',
        actions: [],
      },
      {
        title: 'Do not miss healthcare support',
        description:
          'Medical Card and GP Visit Card eligibility can change quickly when income or household structure changes.',
        actions: [],
      },
      {
        title: 'Check DCA and carers’ supports if needed',
        description:
          'These are often missed for months because parents are too busy coping to even know they exist.',
        actions: [],
      },
      {
        title: 'Use Additional Needs Payment for hardship',
        description:
          'This is the route for essential costs you cannot meet from weekly income.',
        actions: [],
        links: [
          {
            label: 'gov.ie: Additional Needs Payment',
            url: 'https://www.gov.ie/en/department-of-social-protection/services/additional-needs-payment/',
          },
        ],
      },
      {
        title: 'Know the maintenance rule change',
        description:
          'Child maintenance is no longer counted in the means test for social welfare payments. That changes the calculation for a lot of lone-parent households.',
        actions: [],
      },
    ],
  }),
  'co-parenting-hard': buildIrelandOnlyPlaybook({
    slug: 'co-parenting-hard',
    title: 'Co-Parenting When It’s Hard',
    description:
      'Practical tools for communicating when communication is difficult, with guardianship and mediation in mind.',
    icon: '🤝',
    life_event_tags: ['co-parenting', 'separation', 'guardianship'],
    steps: [
      {
        title: 'Separate the parenting relationship from the breakup',
        description:
          'The aim is not perfect harmony. It is workable, child-focused decision-making over time.',
        actions: [],
      },
      {
        title: 'Keep communication factual and written',
        description:
          'Short, neutral, practical messages are usually better than trying to resolve hurt through logistics texts.',
        actions: [],
      },
      {
        title: 'Put important arrangements in writing',
        description:
          'School decisions, handovers, holidays, medical issues, and money create fewer disputes when they are spelled out.',
        actions: [],
      },
      {
        title: 'Check who is a guardian',
        description:
          'This matters in Ireland, especially where the parents were never married. Do not assume both parents have the same legal decision-making rights.',
        actions: [],
        links: [
          {
            label: 'Treoir: Fathers and guardianship',
            url: 'https://treoir.ie/groups/father/',
          },
        ],
      },
      {
        title: 'Use mediation early if direct talks fail',
        description:
          'Mediation is often cheaper, quicker, and less damaging than letting things collapse all the way to court.',
        actions: [],
        links: [
          {
            label: 'Legal Aid Board: Family mediation',
            url: 'https://www.legalaidboard.ie/our-family-mediation-service/',
          },
        ],
      },
      {
        title: 'Keep handovers boring',
        description:
          'Short, predictable, and child-focused is the goal. Save arguments for email or mediation, not the doorstep.',
        actions: [],
      },
      {
        title: 'Protect the child from adult conflict',
        description:
          'Do not use your child as a messenger, witness, or source of emotional support. Even when you are furious, keep the adult business with adults.',
        actions: [],
      },
      {
        title: 'Know when you need a court order',
        description:
          'If contact, guardianship, or day-to-day arrangements simply cannot be stabilised, you may need to apply for a formal order.',
        actions: [],
        links: [
          {
            label: 'Courts: Arrangements for children',
            url: 'https://www.courts.ie/hubs/arrangements-for-children',
          },
        ],
      },
    ],
  }),
  'building-support-network': buildIrelandOnlyPlaybook({
    slug: 'building-support-network',
    title: 'Building Your Support Network',
    description:
      'When you have moved, separated, or just feel like you are doing this alone, these are the Irish support routes worth keeping close.',
    icon: '💗',
    life_event_tags: ['wellbeing', 'community', 'support'],
    steps: [
      {
        title: 'Name what is actually missing',
        description:
          'Adult company, practical help, school-holiday backup, someone to text at night. Different gaps need different solutions.',
        actions: [],
      },
      {
        title: 'Start smaller than you think',
        description:
          'A weekly coffee or one regular outing is usually more powerful than a grand plan that never happens.',
        actions: [],
      },
      {
        title: 'Use the organisations that already exist',
        description:
          'One Family, local Family Resource Centres, community groups, and local parent networks are there to shorten the distance between you and support.',
        actions: [],
        links: [
          { label: 'One Family', url: 'https://onefamily.ie/' },
          {
            label: 'Tusla: Family Resource Centres',
            url: 'https://www.tusla.ie/services/family-community-support/family-resource-centres/',
          },
        ],
      },
      {
        title: 'Use Kindred as a doorway',
        description:
          'Joining one conversation or one event is often the fastest way to stop feeling like support is happening only for other people.',
        actions: [],
      },
      {
        title: 'Accept practical help',
        description:
          'People do not need you to deserve help perfectly. They just need to know what would actually make life easier.',
        actions: [],
      },
      {
        title: 'Add mental health support if the load is heavy',
        description:
          'Aware, Grow, HSE talk therapy, and your GP are not only for crisis. They are there for the long middle stretch too.',
        actions: [],
        links: [{ label: 'Aware', url: 'https://www.aware.ie/support/' }],
      },
      {
        title: 'Expect rebuilding to take time',
        description:
          'A genuine support network is built through repetition. That is normal. It is not a sign you are doing it wrong.',
        actions: [],
      },
    ],
  }),
};

const STATIC_SUPPORT_PLAYBOOK_BUILDERS: Record<string, StaticSupportPlaybookBuilder> = {
  ...IRELAND_HELPER_PACK_BUILDERS,
  'single-dads-starting-point': buildSingleDadsStartingPointPlaybook,
};

const SUPPORT_PLAYBOOK_COUNTRY_BLOCKLIST: Partial<Record<string, string[]>> = {
  AU: ['single-dads-starting-point'],
  NZ: ['single-dads-starting-point'],
  US: ['single-dads-starting-point'],
};

export const isSupportPlaybookAvailableForCountry = (
  slug: string | null | undefined,
  countryCode?: string | null,
) => {
  if (!slug || !hasLiveSupportForCountry(countryCode)) {
    return false;
  }

  return !(countryCode && SUPPORT_PLAYBOOK_COUNTRY_BLOCKLIST[countryCode]?.includes(slug));
};

export const getStaticSupportPlaybook = (
  slug: string | null | undefined,
  countryCode?: string | null,
  postcode?: string | null,
) => {
  if (!slug) return null;
  if (!isSupportPlaybookAvailableForCountry(slug, countryCode)) {
    return null;
  }
  if (countryCode === 'AU') {
    return getAustraliaStaticSupportPlaybook(slug, postcode);
  }
  if (countryCode === 'NZ') {
    return getNewZealandStaticSupportPlaybook(slug);
  }
  if (countryCode === 'US') {
    return getUnitedStatesStaticSupportPlaybook(slug);
  }
  if (!hasLiveSupportForCountry(countryCode)) {
    return null;
  }
  return STATIC_SUPPORT_PLAYBOOK_BUILDERS[slug]?.(countryCode, postcode) ?? null;
};
